import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { format, parseISO } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import DocumentUploader from './DocumentUploader';
import './EditLocationEntry.css';

const EditLocationEntry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState({
    EntryID: '',
    ShippingID: '',
    Location: '',
    DatePlaced: new Date(),
    Notes: '',
    Qty: ''
  });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [isMobile, setIsMobile] = useState(false); // Mobile detection state
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // ---------------- Mobile Device Detection ----------------
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ---------------- Fetch Entry & Documents ----------------
  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/location-entry/${id}`);
        setEntry({
          ...response.data,
          DatePlaced: parseISO(response.data.DatePlaced)
        });
      } catch (error) {
        console.error('Error fetching entry:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchDocuments = async () => {
      try {
        const documentsResponse = await axios.get(`${apiBaseUrl}/api/location-entry/${id}/documents`);
        setDocuments(documentsResponse.data);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setDocuments([]);
        } else {
          console.error('Error fetching documents:', error);
        }
      }
    };

    fetchEntry();
    fetchDocuments();
  }, [id, apiBaseUrl]);

  // ---------------- Fetch Location Options ----------------
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/api/locations`);
        const options = response.data.map(loc => ({
          value: loc.LocationName,
          label: loc.LocationName,
        }));
        setLocations(options);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };

    fetchLocations();
  }, [apiBaseUrl]);

  // ---------------- Refresh Documents ----------------
  const refreshDocuments = async () => {
    try {
      const documentsResponse = await axios.get(`${apiBaseUrl}/api/location-entry/${id}/documents`);
      setDocuments(documentsResponse.data);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setDocuments([]);
      } else {
        console.error('Error refreshing documents:', error);
      }
    }
  };

  // ---------------- Standard Field Handlers ----------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEntry({ ...entry, [name]: value });
  };

  const handleDateChange = (date) => {
    setEntry({ ...entry, DatePlaced: date });
  };

  const handleLocationChange = (selectedOption) => {
    setEntry({ ...entry, Location: selectedOption ? selectedOption.value : '' });
  };

  // ---------------- Save Location Entry ----------------
  const handleSave = async () => {
    try {
      await axios.put(`${apiBaseUrl}/api/location-entry/update/${id}`, {
        ...entry,
        DatePlaced: format(entry.DatePlaced, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
      });
      navigate(`/shipping-item/${entry.ShippingID}`);
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry: ' + error.message);
    }
  };

  // ---------------- Document Download / Delete Handlers ----------------
  const handleDownload = async (fileName) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/download-item/${id}`, {
        params: { fileName },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleDeleteFile = async (fileName) => {
    try {
      await axios.delete(`${apiBaseUrl}/api/location-entry-documentation/${id}/file`, {
        data: { fileName }
      });
      alert('File deleted successfully');
      refreshDocuments();
    } catch (error) {
      console.error('Error deleting file:', error.response ? error.response.data : error.message);
      alert('File delete failed');
    }
  };

  // ---------------- Delete Entire Location Entry ----------------
  const handleDeleteLocation = async () => {
    const confirmDelete = window.confirm('Are you sure you want to delete this location entry?');
    if (confirmDelete) {
      try {
        await axios.delete(`${apiBaseUrl}/api/location-entry/${id}`);
        alert('Location entry deleted successfully');
        navigate(`/shipping-item/${entry.ShippingID}`);
      } catch (error) {
        console.error('Error deleting location entry:', error.response ? error.response.data : error.message);
        alert('Failed to delete location entry');
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="edit-location-container">
      <h1>Edit Location Entry</h1>
      <div className="edit-location-card">
        <div className="form-group">
          <label>Location</label>
          <Select
            options={locations}
            onChange={handleLocationChange}
            value={locations.find(option => option.value === entry.Location)}
            placeholder="Select a location"
            isClearable
            isSearchable
            required
          />
        </div>
        <div className="form-group">
          <label>Date Placed</label>
          <DatePicker
            selected={entry.DatePlaced}
            onChange={handleDateChange}
            dateFormat="MMMM d, yyyy"
            className="date-picker"
          />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea
            name="Notes"
            value={entry.Notes}
            onChange={handleInputChange}
          ></textarea>
        </div>
        <div className="form-group">
          <label>Quantity</label>
          <input
            type="number"
            name="Qty"
            value={entry.Qty}
            onChange={handleInputChange}
          />
        </div>

        {/* Documents List */}
        <div className="documents-section">
          <h2>All Documents</h2>
          {documents.length === 0 ? (
            <p>No documents found</p>
          ) : (
            <ul>
              {documents.map((doc) => (
               <li key={doc.DocumentID}>
               <div className="left-part">
                 <span>{doc.FileName}</span>
                 <span className="document-notes">{doc.EntryNotes || 'No Notes Attached'}</span>
               </div>
               <div className="right-part">
                 <button onClick={() => handleDownload(doc.FileName)} className="download-button">
                   Download
                 </button>
                 <button onClick={() => handleDeleteFile(doc.FileName)} className="delete-button">
                   Delete
                 </button>
               </div>
             </li>
             
              ))}
            </ul>
          )}
        </div>

        {/* Document Uploader Component */}
        <DocumentUploader
          entryId={id}
          userRole="Admin"
          isMobile={isMobile} // Pass mobile detection prop so uploader can conditionally hide file upload section
          refreshDocuments={refreshDocuments}
        />

        <div className="form-buttons">
          <button onClick={handleSave} className="global-save-button">Save Changes</button>
          <button onClick={handleBack} className="global-back-button">Back</button>
          <button onClick={handleDeleteLocation} className="global-delete-button">Delete Location</button>
        </div>
      </div>
    </div>
  );
};

export default EditLocationEntry;
